import React, { useState } from "react";
import { Package, Search, Truck, ExternalLink, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { trackOrder } from "../lib/api";

const Field = ({ label, testid, ...props }) => (
  <div>
    <label className="font-display text-[10px] tracking-[0.3em] text-white/40 block mb-2">
      {label.toUpperCase()}
    </label>
    <input
      {...props}
      data-testid={testid}
      className="w-full bg-transparent border-b border-white/15 focus:border-[#D10000] outline-none py-3 text-white placeholder:text-white/30"
    />
  </div>
);

export default function Track() {
  const [sessionId, setSessionId] = useState("");
  const [email, setEmail] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!sessionId && !email) {
      toast.error("Enter an order number or email.");
      return;
    }
    setLoading(true);
    setOrder(null);
    try {
      const res = await trackOrder({
        session_id: sessionId.trim() || undefined,
        email: email.trim() || undefined,
      });
      setOrder(res);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No order found.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#050505] text-white" data-testid="track-page">
      <section className="max-w-[1100px] mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-12 border-b border-white/10">
        <div className="font-display text-xs tracking-[0.3em] text-[#D10000] mb-4">
          ORDER TRACKING
        </div>
        <h1
          className="font-display uppercase leading-[0.9] tracking-tight"
          style={{ fontSize: "clamp(2.5rem, 7vw, 6rem)" }}
        >
          Track <br /> <span className="text-stroke">your order.</span>
        </h1>
        <p className="mt-6 max-w-xl text-white/65 leading-relaxed">
          Enter the order number from your confirmation email (or the email
          you used at checkout) to see status, tracking number, and carrier.
        </p>
      </section>

      <section className="max-w-[1100px] mx-auto px-6 md:px-12 py-16">
        <form
          onSubmit={onSubmit}
          className="bg-[#0A0A0A] border border-white/10 p-8 md:p-12 grid grid-cols-1 md:grid-cols-2 gap-8"
          data-testid="track-form"
        >
          <Field
            label="Order Number (session id)"
            testid="track-session-input"
            placeholder="cs_test_..."
            value={sessionId}
            onChange={(e) => setSessionId(e.target.value)}
          />
          <Field
            label="OR Email Used at Checkout"
            type="email"
            testid="track-email-input"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <div className="md:col-span-2">
            <button
              type="submit"
              disabled={loading}
              data-testid="track-submit-button"
              className="inline-flex items-center gap-3 bg-[#D10000] hover:bg-[#A30000] disabled:opacity-50 text-white font-display text-lg tracking-widest uppercase px-8 py-4 transition-colors"
            >
              <Search className="h-4 w-4" />
              {loading ? "Looking up…" : "Track Order"}
            </button>
          </div>
        </form>

        {order && (
          <article
            className="mt-10 bg-[#0A0A0A] border border-white/10 p-8 md:p-12"
            data-testid="track-result"
          >
            <div className="flex items-start justify-between gap-6 flex-wrap mb-8">
              <div>
                <div className="font-display text-xs tracking-[0.3em] text-[#D10000] mb-2">
                  ORDER · {order.id?.slice(0, 8).toUpperCase()}
                </div>
                <h2 className="font-display text-3xl md:text-4xl tracking-tight uppercase">
                  {order.tracking_number ? "In transit." : "Confirmed."}
                </h2>
                <p className="text-white/55 text-sm mt-2">
                  Placed{" "}
                  {order.created_at &&
                    new Date(order.created_at).toLocaleString("en-US", {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                </p>
              </div>
              <div className="text-right">
                <div className="font-display text-3xl tracking-wider">
                  ${Number(order.amount || 0).toFixed(2)}
                </div>
                <div className="text-white/40 text-xs uppercase tracking-widest">
                  {(order.currency || "usd").toUpperCase()}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <Block
                Icon={CheckCircle2}
                label="Payment"
                value="Confirmed"
              />
              <Block
                Icon={Truck}
                label="Shipping"
                value={
                  order.shipping_rate
                    ? `${order.shipping_rate.provider} · ${
                        order.shipping_rate.servicelevel_name || "Standard"
                      }`
                    : "Pending"
                }
              />
              <Block
                Icon={Package}
                label="Tracking #"
                value={order.tracking_number || "Pending label"}
              />
              <Block
                Icon={ExternalLink}
                label="Carrier Link"
                value={
                  order.tracking_url ? (
                    <a
                      href={order.tracking_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[#D10000] hover:underline"
                      data-testid="carrier-link"
                    >
                      Open in carrier site →
                    </a>
                  ) : (
                    "Available once shipped"
                  )
                }
              />
            </div>

            {order.shipping_address && (
              <div className="border-t border-white/10 pt-6 mb-8">
                <div className="font-display text-[10px] tracking-[0.3em] text-white/40 mb-2">
                  SHIPPING TO
                </div>
                <p className="text-white/80">
                  {order.shipping_address.name} · {order.shipping_address.city},{" "}
                  {order.shipping_address.state} {order.shipping_address.zip}
                </p>
              </div>
            )}

            <div className="border-t border-white/10 pt-6">
              <div className="font-display text-[10px] tracking-[0.3em] text-white/40 mb-3">
                ITEMS
              </div>
              <ul className="space-y-2">
                {(order.items || []).map((i, k) => (
                  <li
                    key={k}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="text-white/85">
                      {i.product_id} × {i.quantity}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </article>
        )}
      </section>
    </div>
  );
}

const Block = ({ Icon, label, value }) => (
  <div className="flex items-start gap-4 p-5 border border-white/10 bg-[#070707]">
    <Icon className="h-5 w-5 text-[#D10000] mt-1 shrink-0" />
    <div>
      <div className="font-display text-[10px] tracking-[0.3em] text-white/40 mb-1">
        {label}
      </div>
      <div className="text-white/85 text-sm">{value}</div>
    </div>
  </div>
);
